import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { useAuth } from './useAuth'
import { removeProfileFromFeedCache } from '../feed/removeProfileFromFeedCache'

export interface FeedProfile {
  userId: string
  name: string
  age: number
  city: string
  bio?: string
  photos: Array<{ url: string; thumbUrl: string }>
  interests: Array<{ id: number; title: string; emoji?: string }>
  height?: number
  education?: string
  occupation?: string
  /** Номер подтверждён через MAX (requestContact + сервер). */
  isPhoneVerified?: boolean
}

export function useFeed(
  limit: number = 10,
  options?: { refetchOnMount?: boolean | 'always' },
) {
  const userId = useAuth((s) => s.user?.id ?? null)
  return useQuery({
    queryKey: ['feed', limit, userId ?? ''],
    queryFn: () => apiClient.get<FeedProfile[]>(`/api/feed?limit=${limit}`),
    enabled: Boolean(userId),
    refetchOnMount: options?.refetchOnMount ?? true,
    refetchOnWindowFocus: true,
  })
}

export interface FeedReviewEntry {
  userId: string
  action: string
  at: string
  name: string
  age: number | null
  city: string
  thumbUrl: string | null
  /** Реакция этого человека на тебя: ещё нет / пропуск / лайк (в т.ч. суперлайк). */
  theirReaction?: 'none' | 'skip' | 'like'
}

export function useFeedReview(kind: 'skipped' | 'liked') {
  const userId = useAuth((s) => s.user?.id ?? null)
  return useQuery({
    queryKey: ['feed-review', kind, userId ?? ''],
    queryFn: () =>
      apiClient.get<FeedReviewEntry[]>(`/api/feed/review?kind=${encodeURIComponent(kind)}`),
    enabled: Boolean(userId),
    refetchOnMount: 'always',
  })
}

export function useRecordAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { toUserId: string; action: 'LIKE' | 'SKIP'; message?: string }) =>
      apiClient.post('/api/feed/action', data),
    onMutate: async ({ toUserId }) => {
      await queryClient.cancelQueries({ queryKey: ['feed'] })
      const snapshots = queryClient.getQueriesData<FeedProfile[]>({ queryKey: ['feed'] })
      queryClient.setQueriesData<FeedProfile[]>({ queryKey: ['feed'] }, (old) =>
        removeProfileFromFeedCache(old, toUserId),
      )
      return { snapshots }
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['feed-review'] })
    },
  })
}
